import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Gamepad2,
  Map,
  MapPin,
  Play,
  Route,
  Smartphone,
  ListOrdered,
  type LucideIcon,
} from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/en';
import {
  displaySpeed,
  MAP_THEMES,
  type AppDefaults,
  type DefaultCategoryId,
} from './app-defaults-model';
import { SettingsPath } from './SettingsChrome';
import { defaultsCategoryPath, SETTINGS_HOME } from './settings-paths';
import { useAppDefaults } from './use-app-defaults';
import './settings-home.css';

const TILES: ReadonlyArray<{
  id: DefaultCategoryId;
  tone: string;
  icon: LucideIcon;
  title: MessageKey;
  desc: MessageKey;
}> = [
  { id: 'map', tone: 'map', icon: Map, title: 'defaults.map.title', desc: 'defaults.map.desc' },
  {
    id: 'stationary',
    tone: 'stationary',
    icon: MapPin,
    title: 'defaults.stationary.title',
    desc: 'defaults.stationary.desc',
  },
  {
    id: 'playlist',
    tone: 'playlist',
    icon: ListOrdered,
    title: 'defaults.playlist.title',
    desc: 'defaults.playlist.desc',
  },
  {
    id: 'itinerary',
    tone: 'itinerary',
    icon: Route,
    title: 'defaults.itinerary.title',
    desc: 'defaults.itinerary.desc',
  },
  {
    id: 'joystick',
    tone: 'joystick',
    icon: Gamepad2,
    title: 'defaults.joystick.title',
    desc: 'defaults.joystick.desc',
  },
];

function summary(defaults: AppDefaults, id: DefaultCategoryId, t: (key: MessageKey, vars?: Record<string, string | number>) => string): string {
  if (id === 'map') {
    const theme = MAP_THEMES.find((item) => item.id === defaults.mapTheme);
    return `${theme ? t(theme.labelKey) : ''} · ${t('defaults.summary.zoom', { n: defaults.mapZoom })}`;
  }
  if (id === 'stationary') {
    const view = t(defaults.stationaryView === 'GRID' ? 'defaults.view.grid' : 'defaults.view.list');
    return `${t('defaults.meters', { n: defaults.stationaryAccuracy })} · ${view}`;
  }
  if (id === 'playlist') {
    return `${t('defaults.seconds', { n: defaults.playlistTimeSec })} · ${t('defaults.meters', { n: defaults.playlistAccuracy })}`;
  }
  if (id === 'itinerary') {
    const unit = defaults.itinerarySpeedUnit;
    return t('defaults.speedValue', {
      n: displaySpeed(defaults.itinerarySpeedKmh, unit),
      unit: t(unit === 'mph' ? 'defaults.unit.mph' : 'defaults.unit.kmh'),
    });
  }
  if (id === 'joystick') {
    const unit = defaults.joystickSpeedUnit;
    const marker =
      defaults.joystickMarker === 'CAR'
        ? t('defaults.marker.car')
        : defaults.joystickMarker === 'WAZE'
          ? t('defaults.marker.waze')
          : t('defaults.marker.maps');
    const speed = t('defaults.speedValue', {
      n: displaySpeed(defaults.joystickSpeedKmh, unit),
      unit: t(unit === 'mph' ? 'defaults.unit.mph' : 'defaults.unit.kmh'),
    });
    return `${speed} · ${marker}`;
  }
  const on = [
    defaults.overlayStationary,
    defaults.overlayPlaylist,
    defaults.overlayItinerary,
    defaults.overlayJoystick,
  ].filter(Boolean).length;
  if (on === 4) return t('defaults.summary.playerAll');
  if (on === 0) return t('defaults.summary.playerOff');
  return t('defaults.summary.playerSome', { on });
}

const AppDefaultsPage: React.FC = () => {
  const { t } = useLanguage();
  const { defaults } = useAppDefaults();

  return (
    <div className="set">
      <SettingsPath
        items={[
          { label: t('settings.back'), to: SETTINGS_HOME },
          { label: t('settings.home.defaults.title') },
        ]}
      />
      <div className="set-banner">
        <span className="set-ico set-ico--phone">
          <Smartphone size={20} aria-hidden="true" />
        </span>
        <p>{t('defaults.syncNote')}</p>
      </div>

      <div className="set-section__head">
        <h2>{t('defaults.sectionDefaults')}</h2>
        <p>{t('defaults.sectionDefaultsDesc')}</p>
      </div>
      <div className="set-grid">
        {TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <Link key={tile.id} className="set-tile" to={defaultsCategoryPath(tile.id)}>
              <span className={`set-ico set-ico--${tile.tone}`}>
                <Icon size={20} aria-hidden="true" />
              </span>
              <span className="set-tile__copy">
                <span className="set-tile__title">{t(tile.title)}</span>
                <span className="set-tile__desc">{t(tile.desc)}</span>
                <span className="set-tile__value">{summary(defaults, tile.id, t)}</span>
              </span>
            </Link>
          );
        })}
      </div>

      <div className="set-section__head">
        <h2>{t('defaults.sectionPlayer')}</h2>
        <p>{t('defaults.sectionPlayerDesc')}</p>
      </div>
      <Link className="set-row" to={defaultsCategoryPath('player')}>
        <span className="set-ico set-ico--player">
          <Play size={20} aria-hidden="true" />
        </span>
        <span className="set-row__copy">
          <span className="set-row__title">{t('defaults.player.title')}</span>
          <span className="set-row__desc">{t('defaults.player.desc')}</span>
        </span>
        <span className="set-row__meta">{summary(defaults, 'player', t)}</span>
        <ChevronRight className="set-chevron" size={18} aria-hidden="true" />
      </Link>
    </div>
  );
};

export default AppDefaultsPage;
